import React from 'react';
import type { PriorAuthorizationClaimContext } from '@features/patient-coverages/types';
import FactCheckOutlined from '@mui/icons-material/FactCheckOutlined';
import { Alert, Box, Divider, Fade, Popover, Stack, Typography } from '@mui/material';

export interface PriorAuthorizationPopoverProps {
  context: PriorAuthorizationClaimContext;
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
}

export const PriorAuthorizationPopover: React.FC<PriorAuthorizationPopoverProps> = ({
  context,
  anchorEl,
  open,
  onClose,
  onMouseEnter,
  onMouseLeave,
}) => (
  <Popover
    open={open}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    transitionDuration={160}
    slots={{ transition: Fade }}
    sx={{
      pointerEvents: 'none',
      '& .MuiPopover-paper': {
        pointerEvents: 'auto',
        width: 360,
        maxWidth: 'calc(100vw - 24px)',
        mt: 0.5,
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'borderColors.primary',
        boxShadow: 3,
      },
    }}
  >
    <Box onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
      <Stack direction="row" spacing={1} sx={{ p: 1.5, bgcolor: 'backgroundColors.secondary' }}>
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: 1,
            bgcolor: 'primary.main',
            color: 'primary.contrastText',
            display: 'grid',
            placeItems: 'center',
            flexShrink: 0,
          }}
        >
          <FactCheckOutlined sx={{ fontSize: 16 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 750 }}>Authorization progress</Typography>
          <Typography color="text.secondary" variant="caption">
            For this visit’s date and billed service
          </Typography>
        </Box>
      </Stack>
      <Divider />
      <Stack spacing={1.25} sx={{ p: 1.5 }}>
        {context.lookup_state === 'conflict' ? (
          <Alert severity="warning">
            More than one authorization matches. Review the authorization records before relying on
            a count.
          </Alert>
        ) : null}
        {context.course?.documented_label ? (
          <Box>
            <Typography color="text.secondary" variant="caption">
              Documented
            </Typography>
            <Typography sx={{ fontWeight: 750 }}>{context.course.documented_label}</Typography>
          </Box>
        ) : null}
        {context.course?.billed_label ? (
          <Box>
            <Typography color="text.secondary" variant="caption">
              Billed
            </Typography>
            <Typography sx={{ fontWeight: 750 }}>{context.course.billed_label}</Typography>
          </Box>
        ) : null}
        {context.course?.seed_explanation ? (
          <Box>
            <Typography color="text.secondary" variant="caption">
              Count basis
            </Typography>
            <Typography variant="body2">{context.course.seed_explanation}</Typography>
          </Box>
        ) : null}
        {context.course ? (
          <>
            <Divider />
            <Typography variant="body2">
              <strong>Authorization:</strong> {context.course.auth_number ?? 'No number recorded'}
            </Typography>
            <Typography variant="body2">
              <strong>Window:</strong> {context.course.auth_start_date} –{' '}
              {context.course.auth_end_date ?? 'open ended'}
            </Typography>
          </>
        ) : null}
      </Stack>
    </Box>
  </Popover>
);
